import React, { createContext, useContext, useState, useEffect } from 'react';
import { getPublicAppMaintenanceStatus } from '../../api/system/systemStatus';
import { setAppMaintenanceMode } from '../api/systemStatusApi';

const MaintenanceContext = createContext();
const POLL_MS = 12000;

export const useMaintenanceMode = () => {
  const context = useContext(MaintenanceContext);
  if (!context) {
    throw new Error('useMaintenanceMode must be used within a MaintenanceProvider');
  }
  return context;
};

/**
 * @param {Object} props
 * @param {React.ReactNode} props.children
 * @param {boolean} [props.canWrite] - Admin panel can persist toggles; the main app only reads status.
 */
export const MaintenanceProvider = ({ children, adminFunctions, canWrite = Boolean(adminFunctions) }) => {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);
  const [scheduledStartTime, setScheduledStartTime] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const status = await getPublicAppMaintenanceStatus();
        if (cancelled) return;
        setIsMaintenanceMode(Boolean(status?.enabled));
        setScheduledStartTime(status?.scheduledStartTime ?? null);
      } catch (error) {
        console.error('Error loading maintenance status:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    const interval = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  const persist = async ({ enabled, scheduledStartTime: sst }) => {
    if (!canWrite) return false;
    try {
      const payload = {};
      if (enabled !== undefined) payload.enabled = enabled;
      if (sst !== undefined) payload.scheduledStartTime = sst;
      const result = await setAppMaintenanceMode(payload);
      setIsMaintenanceMode(Boolean(result.enabled));
      if ('scheduledStartTime' in result) {
        setScheduledStartTime(result.scheduledStartTime ?? null);
      }
      return true;
    } catch (error) {
      console.error('Error updating maintenance:', error);
      return false;
    }
  };

  const toggleMaintenanceMode = async (scheduledStartTimePayload = null) => {
    return persist({
      enabled: !isMaintenanceMode,
      scheduledStartTime: scheduledStartTimePayload ?? scheduledStartTime,
    });
  };

  const setAppMaintenance = async ({ enabled, scheduledStartTime: sst }) => {
    return persist({
      enabled: enabled ?? isMaintenanceMode,
      scheduledStartTime: sst !== undefined ? sst : scheduledStartTime,
    });
  };

  const value = {
    isMaintenanceMode,
    scheduledStartTime,
    toggleMaintenanceMode,
    setAppMaintenance,
    loading,
  };

  return (
    <MaintenanceContext.Provider value={value}>
      {children}
    </MaintenanceContext.Provider>
  );
};
